import { ReactNode, createRef, useEffect, useRef, useState } from 'react';

import PageHead from '@/components/PageHead';
import PageLayout from '@/components/layout/page-layout';
import Container from '@/components/Container';
import HeadingOne from '@/components/Heading/HeadingOne';
import { ExtendProps } from 'app';

import NextImage from 'next/image';
import NextLink from 'next/link';

import { FiExternalLink } from 'react-icons/fi';

export default function Page() {
	const [isOpenDetails, setIsOpenDetails] = useState(
		Array.from({ length: 8 }, (ref, k) => false)
	);

	return (
		<>
			<PageHead
				page={{
					title: 'Instituto Deixe Vivo | O que fazer na falta de Medicamentos',
					keywords:
						'OSC, Instituto, Deixe Vivo, Doação de órgãos e tecidos, transplantes, renal, tecidos, história, trajetória',
					colorScheme: 'light dark',
					description: 'Instituto Deixe Vivo',
					robots: 'nofollow, noindex',
				}}
				og={{
					description:
						'Instituto Deixe Vivo | O que fazer na falta de Medicamentos',
					site_name:
						'Instituto Deixe Vivo | O que fazer na falta de Medicamentos',
					title: 'Instituto Deixe Vivo | O que fazer na falta de Medicamentos',
					type: 'website',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						fbAdmins: 'instituto.deixevivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
						type: 'image/jpg',
						width: '1200',
						height: '1200',
					},
				}}
				twitter={{
					card: 'summary',
					description:
						'Instituto Deixe Vivo | O que fazer na Falta de Medicamentos',
					site: 'Instituto Deixe Vivo | O que fazer na Falta de Medicamentos',
					title: 'Instituto Deixe Vivo | O que fazer na Falta de Medicamentos',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
					},
				}}
			/>
			<PageLayout>
				<Container className="py-8">
					<div className="relative">
						<HeadingOne horizontalRow>Medicação de Alto Custo</HeadingOne>
						<NextImage
							className="float-right"
							width={128}
							height={128}
							src="/assets/images/illustrations/medicamentos-alto-custo.png"
							alt="Medicação de Alto Custo"
						/>
					</div>

					<section className="mt-4">
						<p>
							Os transplantados fazem uso de medicação contínua para manter o
							seu enxerto (órgão) transplantado saudável. E muitas dessas
							medicações são fornecidas pelas{' '}
							<span className="uppercase">FARMÁCIAS DE ALTO CUSTO do SUS.</span>
						</p>
						<br />
						<p>
							No pós transplante, antes da alta hospitalar, o paciente recebe
							todas as orientações e documentações necessárias para dar entrada
							no pedido das medicações na farmácia de alto custo mais próxima de
							sua residência.
						</p>
						<br />
						<p>
							Infelizmente, sabemos que não há regularidade na entrega dos
							medicamentos, pela falta e/ou pela demanda maior que a oferta. Por
							isso, alguns pacientes ficam sem receber as medicações no período
							certo e é necessário recorrer a outros pacientes que possam doar o
							medicamento ou ainda, comprá-los... O grande problema é que além
							de serem remédios caros, ainda há certa dificuldade em
							encontrá-los para venda.
						</p>
						<br />
						<p>
							Diante desse cenário, entendemos que é extremamente importante
							EDUCAR os pacientes para que conheçam os seus direitos e saibam o
							que fazer quando houver falta dos medicamentos que são
							imprescindíveis para manter a saúde do transplante.
						</p>
					</section>

					<section className="mt-8">
						<h2 className="text-2xl font-bold mb-4">
							Saiba o que fazer na falta de medicamentos de alto custo:
						</h2>

						<ul className="list-disc">
							<li>
								Tomar a medicação de acordo com a prescrição faz parte do
								sucesso do seu tratamento, portanto, caso não receba a medicação
								na quantidade correta, não diminua a dosagem, nem alterne os
								dias para que a medicação se encaixe no mês. Entre em contato
								imediato com a sua equipe de transplante para receber
								orientações.
							</li>
							<br />
							<li>
								Quando receber os medicamentos, peça o recibo de entrega e
								verifique na planilha da farmácia qual foi a quantidade enviada
								efetivamente no seu caso, peça para fazer uma cópia ou
								fotografe.
							</li>
							<br />
							<li>
								Verifique nas redes sociais ou grupos onde há doações entre
								transplantados (não se esqueça de anexar sua receita, para que
								não haja desconfianças). Nesse caso é necessário combinar de que
								forma será realizada a entrega do medicamento, pessoalmente,
								motoboy, Uber ou Sedex. (Lembrando que a venda de medicações de
								alto custo sem regulamentação, ou seja, sem ser em farmácias
								referenciadas, é <span className="uppercase">crime!</span>)
							</li>
							<br />
							<li>
								Informe a falta de medicação no site da ABTx, esse registro é
								necessário para que eles entrem em contato com o Ministério e
								Secretarias de Saúde para cobrar explicações e pressionar o
								reabastecimento o mais rápido possível.
								<NextLink
									className="flex text-sky-700"
									href="https://www.abtx.com.br/registrofaltamedicamento"
									target="_blank"
									title="Link para registro de falta de medicamento"
								>
									Faça seu registro aqui!
									<FiExternalLink className="ml-1" />
								</NextLink>
							</li>
							<br />
							<li>
								Outros canais que devem ser acionados na falta de medicações ou
								quando a entrega for realizada de modo parcial (fracionada):
								<ul className="list-inside [list-style-type:circle]">
									<li>
										Diretoria Regional de Saúde (DRS) do qual seu município faz
										parte
									</li>
									<li>Defensoria Pública (atendimento on-line).</li>
									<li>Ouvidoria do SUS: Via formulário online ou telefone.</li>
								</ul>
							</li>
						</ul>
					</section>
				</Container>
			</PageLayout>
		</>
	);
}
